import { AlertDialog, Button } from "@pej/kore/components";
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

/**
 * One-section-at-a-time editing for a survey's detail page, ported from the
 * vouchers admin chassis (`features/voucher-programs/chassis/section-edit-controller.tsx`
 * + `use-section-edit.ts`, combined here into one file). A page mounts a
 * single `SectionEditProvider`; each section calls `useSectionEdit` to join
 * it. Only one section is ever in edit mode — activating a second while the
 * first is dirty prompts a discard dialog naming the dirty section instead
 * of silently dropping its edits.
 */

export interface SectionRegistration {
    sectionId: string;
    sectionTitle: string;
    isDirty: () => boolean;
    reset: () => void;
}

export interface SectionEditController {
    activeSection: string | null;
    /** Map of sectionId → has-validation-errors. Sections publish their state
     *  via `setSectionErrors`; consumers (the TOC) read it to render the
     *  per-section error dot. Stored as plain state so consumers re-render
     *  when it changes — error toggles fire on blur, not per keystroke. */
    errorsBySection: Record<string, boolean>;
    /** Live getter for "is the currently-active section dirty?" — used by a
     *  page-level navigation guard (`useUnsavedChangesGuard`) mounted by the
     *  detail-page body to block in-app navigation and beforeunload while a
     *  section is mid-edit. Getter form (not a captured boolean) so the
     *  guard reads fresh state on each call. */
    isActiveSectionDirty: () => boolean;
    /** True while any section is in edit mode — the add buttons disable on it
     *  ("Save or cancel the current edit first."). */
    isAnySectionActive: boolean;
    register: (reg: SectionRegistration) => () => void;
    requestActivate: (sectionId: string) => void;
    deactivate: (sectionId: string) => void;
    setSectionErrors: (sectionId: string, hasErrors: boolean) => void;
    /** When true, every section is in read-only mode. Sections gate their
     *  edit-affordance render on `!isReadOnly` rather than relying on the
     *  chassis to silently no-op `requestActivate` — leaving a clickable
     *  dead button violates Nielsen's "minimalist design" heuristic. */
    readOnly: boolean;
}

const Ctx = createContext<SectionEditController | null>(null);

export function useSectionEditController(): SectionEditController {
    const ctx = useContext(Ctx);
    if (!ctx) {
        throw new Error("useSectionEditController must be used inside <SectionEditProvider>");
    }
    return ctx;
}

// Provider scope: one detail page. The discard dialog is mounted once here so
// individual sections don't each carry a copy. Active section bookkeeping is
// kept in state; sections register their `isDirty` + `reset` callbacks via a
// ref-based registry so the provider can ask "is the current active section
// dirty?" without re-rendering the whole tree on every dirty-flag change.
export function SectionEditProvider({ children, readOnly = false }: { children: ReactNode; readOnly?: boolean }) {
    const [active, setActive] = useState<string | null>(null);
    const registry = useRef<Map<string, SectionRegistration>>(new Map());
    const [pending, setPending] = useState<{ next: string; current: SectionRegistration } | null>(null);
    const [errorsBySection, setErrorsBySection] = useState<Record<string, boolean>>({});

    const register = useCallback<SectionEditController["register"]>((reg) => {
        registry.current.set(reg.sectionId, reg);
        return () => {
            registry.current.delete(reg.sectionId);
            setActive((prev) => (prev === reg.sectionId ? null : prev));
            // Drop any error flag the section was carrying — stale entries
            // would keep the TOC dot lit after the card unmounts (e.g. when
            // a draft is cancelled while invalid).
            setErrorsBySection((prev) => {
                if (!(reg.sectionId in prev)) return prev;
                const { [reg.sectionId]: _, ...rest } = prev;
                return rest;
            });
        };
    }, []);

    const setSectionErrors = useCallback<SectionEditController["setSectionErrors"]>((sectionId, hasErrors) => {
        setErrorsBySection((prev) => {
            const current = !!prev[sectionId];
            if (current === hasErrors) return prev; // no-op skip avoids re-renders on blur of a clean field
            if (hasErrors) return { ...prev, [sectionId]: true };
            const { [sectionId]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const requestActivate = useCallback<SectionEditController["requestActivate"]>(
        (sectionId) => {
            // Already active — no-op.
            if (active === sectionId) {
                return;
            }
            const current = active ? registry.current.get(active) : null;
            if (current?.isDirty()) {
                // Defer activation pending dialog confirmation.
                setPending({ next: sectionId, current });
                return;
            }
            // Clean → release immediately.
            if (current) {
                current.reset();
            }
            setActive(sectionId);
        },
        [active],
    );

    const deactivate = useCallback<SectionEditController["deactivate"]>((sectionId) => {
        setActive((prev) => (prev === sectionId ? null : prev));
    }, []);

    const isActiveSectionDirty = useCallback<SectionEditController["isActiveSectionDirty"]>(() => {
        if (!active) return false;
        return registry.current.get(active)?.isDirty() ?? false;
    }, [active]);

    function confirmDiscard() {
        if (!pending) return;
        pending.current.reset();
        setActive(pending.next);
        setPending(null);
    }

    function cancelDiscard() {
        setPending(null);
    }

    const value = useMemo<SectionEditController>(
        () => ({
            activeSection: active,
            errorsBySection,
            isActiveSectionDirty,
            isAnySectionActive: active !== null,
            register,
            requestActivate,
            deactivate,
            setSectionErrors,
            readOnly,
        }),
        [
            active,
            errorsBySection,
            isActiveSectionDirty,
            register,
            requestActivate,
            deactivate,
            setSectionErrors,
            readOnly,
        ],
    );

    return (
        <Ctx.Provider value={value}>
            {children}
            <AlertDialog open={pending !== null} onOpenChange={(open) => !open && cancelDiscard()}>
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Title>Discard changes in {pending?.current.sectionTitle}?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You have unsaved changes in <strong>{pending?.current.sectionTitle}</strong>. Switching to
                            another section discards them.
                        </AlertDialog.Description>
                    </AlertDialog.Header>
                    <AlertDialog.Footer>
                        <Button variant="outline" onClick={cancelDiscard}>
                            Keep editing
                        </Button>
                        <Button variant="destructive-solid" onClick={confirmDiscard}>
                            Discard
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog>
        </Ctx.Provider>
    );
}

export interface UseSectionEditArgs {
    sectionId: string;
    sectionTitle: string;
    isDirty: boolean;
    /** Whether this section currently has validation errors. Drives the small
     *  red dot the TOC paints next to the section. Optional — sections that
     *  don't have a form-level error concept can omit it. */
    hasErrors?: boolean;
    /** Called when the controller releases this section (e.g. user discards). */
    reset: () => void;
}

export interface UseSectionEditReturn {
    isEditing: boolean;
    enterEdit: () => void;
    exitEdit: () => void;
    /** When true, the surrounding `<SectionEditProvider readOnly>` has put
     *  every section into read-only mode (e.g., the survey is archived).
     *  Sections MUST hide their edit-entry affordance on `isReadOnly` —
     *  the chassis does NOT silently no-op `enterEdit`, so a still-rendered
     *  Edit button would appear functional and confuse the user. */
    isReadOnly: boolean;
}

export function useSectionEdit({
    sectionId,
    sectionTitle,
    isDirty,
    hasErrors,
    reset,
}: UseSectionEditArgs): UseSectionEditReturn {
    const ctrl = useSectionEditController();

    // Stash latest dirty/reset behind a ref so the registration the controller
    // holds doesn't go stale between renders.
    const liveRef = useRef({ isDirty, reset });
    useLayoutEffect(() => {
        liveRef.current = { isDirty, reset };
    });

    // ctrl.register is useCallback([])-stable; including the whole `ctrl` would
    // re-register on every render. Re-register only when section identity changes.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
    useEffect(() => {
        return ctrl.register({
            sectionId,
            sectionTitle,
            isDirty: () => liveRef.current.isDirty,
            reset: () => liveRef.current.reset(),
        });
    }, [sectionId, sectionTitle]);

    // Publish error state to the controller so the TOC can render the dot.
    // Tracked separately from the registration because it changes per-keystroke
    // (well, per-blur) — keeping the registration stable lets dirty/reset stay
    // ref-based while errors flow through controlled state.
    // biome-ignore lint/correctness/useExhaustiveDependencies: ctrl.setSectionErrors is useCallback-stable; depending on the whole ctrl would re-register on every render
    useEffect(() => {
        ctrl.setSectionErrors(sectionId, !!hasErrors);
    }, [sectionId, hasErrors]);

    const isEditing = ctrl.activeSection === sectionId;

    return {
        isEditing,
        enterEdit: () => ctrl.requestActivate(sectionId),
        exitEdit: () => ctrl.deactivate(sectionId),
        isReadOnly: ctrl.readOnly,
    };
}
